import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { login } from "../apiCalls/user";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import logoImg from "../assets/images/labzy-logo.webp";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialValues = { mobileNumber: "", password: "" };
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordNew = () => setPasswordShow((p) => !p);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
  };

  const validate = (values) => {
    const errors = {};
    if (!values.mobileNumber) {
      errors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(values.mobileNumber)) {
      errors.mobileNumber = "Enter a valid 10-digit number";
    }
    if (!values.password) {
      errors.password = "Password is required";
    } else if (values.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (values.password.length > 16) {
      errors.password = "Password cannot exceed 16 characters";
    }
    return errors;
  };

  const successCheck = (res) => {
    setIsLoading(false);
    navigate("/");
  };

  const handleClick = () => {
    setIsLoading(true);
    login(dispatch, formValues, successCheck);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(formValues);
    setFormErrors(errors);
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      handleClick();
    }
  }, [formErrors]);

  return (
    <div className="login-page">
      {/* Left panel — brand */}
      <div className="login-brand-panel">
        <div className="login-brand-content">
          <div className="login-logo">
            <img src={logoImg} alt="LabBridge" className="login-logo-img" />
            <span>LabBridge</span>
          </div>
          <h1>Manage your lab smarter.</h1>
          <p>
            Everything you need to run your diagnostic lab — appointments,
            reports, services, and patient records, all in one place.
          </p>
          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-dot" />
              Appointment tracking & scheduling
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot" />
              Report uploads & patient access
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot" />
              Service & pricing management
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot" />
              Reviews & lab ratings
            </div>
          </div>
        </div>

        {/* decorative blobs */}
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />
      </div>

      {/* Right panel — form */}
      <div className="login-form-panel">
        <div className="login-form-card">
          <div className="login-form-header">
            <h2>Welcome back</h2>
            <p>Sign in to your LabBridge account</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Mobile */}
            <div className="lf-group">
              <label htmlFor="mobileNumber">Mobile Number</label>
              <div className={`lf-input-wrap ${formErrors.mobileNumber ? "lf-error" : ""}`}>
                <span className="lf-input-icon">
                  <i className="bi bi-phone" />
                </span>
                <input
                  id="mobileNumber"
                  type="text"
                  name="mobileNumber"
                  placeholder="Enter 10-digit mobile number"
                  value={formValues.mobileNumber}
                  onChange={handleChange}
                  maxLength={10}
                  autoComplete="tel"
                />
              </div>
              {formErrors.mobileNumber && (
                <span className="lf-error-msg">{formErrors.mobileNumber}</span>
              )}
            </div>

            {/* Password */}
            <div className="lf-group">
              <div className="lf-label-row">
                <label htmlFor="password">Password</label>
              </div>
              <div className={`lf-input-wrap ${formErrors.password ? "lf-error" : ""}`}>
                <span className="lf-input-icon">
                  <i className="bi bi-lock" />
                </span>
                <input
                  id="password"
                  type={passwordShow ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={formValues.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lf-eye-btn"
                  onClick={togglePasswordNew}
                  aria-label={passwordShow ? "Hide password" : "Show password"}
                >
                  {passwordShow ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formErrors.password && (
                <span className="lf-error-msg">{formErrors.password}</span>
              )}
              <Link to="/forget-password" className="lf-forgot">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className={`lf-submit-btn ${isLoading ? "lf-loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="lf-spinner" />
                  Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="lf-footer">
            <span>Don't have an account?</span>
            <Link to="/signup">Create account</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
